import type { FraudFlagRow, Page } from '@fieldvision/contracts';
import { ShieldAlert, ShieldCheck } from 'lucide-react';

import {
  Badge,
  EmptyState,
  Panel,
  PanelHeader,
  PanelLink,
  Spinner,
} from '../../components/ui/index.js';
import { dateOnly, integer } from '../../lib/format.js';

/**
 * FR-16 flagged visits awaiting review.
 *
 * CARDS, NOT A TABLE — the one place on this dashboard where the console's usual
 * answer is inverted, matching `FraudQueuePage` for the same reason. A flag is a
 * list of evidence sentences of varying length that a supervisor has to READ
 * before acting. A grid row would truncate exactly the thing the panel exists to
 * show, and nobody can act on `GPS_OUT_OF_TOLERANCE` as a cell value.
 *
 * NO DISPOSITION BUTTONS HERE, deliberately. Setting a disposition feeds HR
 * evaluation; it gets made on `/fraud`, where the full evidence for every signal
 * is on screen. A one-click "Wajar" beside a truncated summary is how a review
 * process becomes a formality.
 *
 * UNRESOLVED, NOT UNTOUCHED. The panel shows PENDING and FOLLOW_UP together.
 * Counting only PENDING reported an empty queue as soon as every case had been
 * picked up and none had been finished, which is precisely the state a manager
 * most needs to see.
 */
export function FlaggedPanel({
  data,
  isLoading,
  isFetching,
  disabled,
}: {
  data: Page<FraudFlagRow> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  disabled: boolean;
}) {
  const flags = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <Panel className="flex h-full flex-col">
      <div className="flex items-start gap-2.5">
        <ShieldAlert size={18} className="mt-1 shrink-0 text-steel" aria-hidden />
        <div className="min-w-0 flex-1">
          <PanelHeader
            title="Kunjungan Terflag"
            description={
              total > 0
                ? `${integer(total)} belum selesai. Sinyal bukan tuduhan — baca buktinya.`
                : 'Sinyal otomatis yang perlu ditinjau manusia.'
            }
            action={<PanelLink to="/fraud" />}
          />
        </div>
      </div>

      {disabled ? (
        <EmptyState title="Rentang tanggal belum valid" icon={ShieldAlert} />
      ) : isLoading ? (
        <Spinner label="Memuat kunjungan terflag…" />
      ) : flags.length === 0 ? (
        <EmptyState
          title="Tidak ada yang belum selesai"
          description="Setiap sinyal pada rentang ini sudah dituntaskan."
          icon={ShieldCheck}
        />
      ) : (
        /*
         * `grid-cols-1` IS NOT REDUNDANT. Without an explicit template the single
         * column below `sm` is an implicit `auto` track, whose minimum is the
         * cards' min-content width — and the two `truncate` lines below are
         * `white-space: nowrap`, so that minimum is the FULL name and outlet
         * string plus the badge. On a phone the track came out wider than the
         * panel, every card spilled past its right edge, and the whole overview
         * scrolled sideways. `grid-cols-1` is `minmax(0, 1fr)`: the track can
         * now shrink and the truncation does its job. `min-w-0` on the card is
         * the same guarantee stated on the item.
         */
        <ul
          className={`grid flex-1 grid-cols-1 gap-stack transition-opacity duration-150 sm:grid-cols-2 ${
            isFetching ? 'opacity-60' : ''
          }`}
        >
          {flags.map((flag) => {
            const [first, ...rest] = flag.signals;
            return (
              <li key={flag.id} className="min-w-0 rounded-2xl border border-line bg-white p-4">
                {/*
                  Wrapping header, same shape as the `/fraud` cards: on a phone
                  the pill drops under the name rather than leaving the outlet
                  ~130px to truncate in. The lines still `truncate` — at the
                  width they actually get.
                */}
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="truncate font-display font-bold text-navy">{flag.salesUserName}</p>
                    <p className="mt-1 truncate text-sm text-muted">
                      {flag.outletName} · {dateOnly(flag.visitDate)}
                    </p>
                  </div>
                  <Badge tone={flag.disposition === 'FOLLOW_UP' ? 'warn' : 'neutral'}>
                    {flag.disposition === 'FOLLOW_UP' ? 'Tindak lanjut' : 'Belum ditinjau'}
                  </Badge>
                </div>

                {first ? (
                  <p className="mt-3 rounded-xl bg-warn-surface p-3 text-sm text-warn">
                    {first.detail}
                    {rest.length > 0 ? (
                      <span className="mt-1 block font-semibold">
                        +{rest.length} sinyal lain
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
